/**
 * Typed endpoint helpers that wrap `request()` with the exact contracts
 * exposed by the Sajha Social backend (internal/handler/*.go).
 *
 * Every helper returns just the `data` (or the full `ApiResult` when
 * pagination metadata is meaningful).
 */

import { request, ApiResult } from "./client";
import type {
  User,
  Post,
  Comment,
  Company,
  CompanyType,
  Event,
  EventParticipant,
  EventRSVPStatus,
  Inquiry,
  InquiryMessage,
  Location,
  PostType,
} from "../types";

// --------------------------------------------------------------------------
// auth
// --------------------------------------------------------------------------

export interface SignupRequest {
  email: string;
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  agree_to_terms: boolean;
}

export interface SignupResponse {
  user: User;
  dev_code?: string;
  dev_code_notes?: string;
}

export interface LoginRequest {
  email_or_username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  expires_at: string;
  user: User;
}

export const auth = {
  signup: (body: SignupRequest) =>
    request<SignupResponse>("/auth/signup", { method: "POST", body, anonymous: true }).then(r => r.data),

  login: (body: LoginRequest) =>
    request<LoginResponse>("/auth/login", { method: "POST", body, anonymous: true }).then(r => r.data),

  verifyEmail: (body: { email_or_username: string; code: string }) =>
    request<void>("/auth/verify-email", { method: "POST", body, anonymous: true }).then(r => r.message),

  resendVerification: (body: { email_or_username: string }) =>
    request<{ message?: string; dev_code?: string }>("/auth/resend-verification", { method: "POST", body, anonymous: true }).then(r => r.data),

  forgotPassword: (body: { email_or_username: string }) =>
    request<{ message?: string; dev_code?: string; dev_code_notes?: string }>("/auth/forgot-password", {
      method: "POST",
      body,
      anonymous: true,
    }).then(r => r.data),

  resetPassword: (body: { email_or_username: string; code: string; new_password: string }) =>
    request<void>("/auth/reset-password", { method: "POST", body, anonymous: true }).then(r => r.message),
};

// --------------------------------------------------------------------------
// users
// --------------------------------------------------------------------------

export const users = {
  me: () => request<User>("/me").then(r => r.data),
  byUsername: (username: string) =>
    request<User>(`/users/${encodeURIComponent(username)}`).then(r => r.data),
  updateMe: (body: { first_name: string; last_name: string; bio: string }) =>
    request<User>("/me", { method: "PATCH", body }).then(r => r.data),
  getSettings: () => request<{ user_id: number; location_id?: number; timezone: string }>("/me/settings").then(r => r.data),
  upsertSettings: (body: { location_id?: number | null; timezone?: string }) =>
    request<{ user_id: number; location_id?: number; timezone: string }>("/me/settings", {
      method: "PUT",
      body,
    }).then(r => r.data),
};

// --------------------------------------------------------------------------
// posts
// --------------------------------------------------------------------------

export interface PostFilter {
  post_type?: string;
  creator_id?: number;
  location_id?: number;
  q?: string;
  page?: number;
  limit?: number;
}

export const posts = {
  list: (f: PostFilter = {}) =>
    request<Post[]>("/posts", { query: { ...f }, anonymous: false }),

  get: (id: number) => request<Post>(`/posts/${id}`).then(r => r.data),

  create: (body: { content: string; post_type: PostType; is_anonymous?: boolean; location_id?: number | null }) =>
    request<Post>("/posts", { method: "POST", body }).then(r => r.data),

  update: (id: number, body: { content: string; post_type: PostType }) =>
    request<Post>(`/posts/${id}`, { method: "PATCH", body }).then(r => r.data),

  remove: (id: number) => request<void>(`/posts/${id}`, { method: "DELETE" }),

  like: (id: number) => request<void>(`/posts/${id}/like`, { method: "POST" }),
  unlike: (id: number) => request<void>(`/posts/${id}/like`, { method: "DELETE" }),

  listComments: (id: number) =>
    request<Comment[]>(`/posts/${id}/comments`).then(r => r.data ?? []),

  addComment: (id: number, body: { content: string }) =>
    request<{ id: number }>(`/posts/${id}/comments`, { method: "POST", body }).then(r => r.data),
};

// --------------------------------------------------------------------------
// companies
// --------------------------------------------------------------------------

export interface CompanyFilter {
  company_type?: string;
  owner_id?: number;
  location_id?: number;
  q?: string;
  page?: number;
  limit?: number;
}

export interface CreateCompanyBody {
  name: string;
  description: string;
  location_id?: number | null;
  phone_number?: string | null;
  email?: string | null;
  company_type_id: number;
}

export const companies = {
  list: (f: CompanyFilter = {}) =>
    request<Company[]>("/companies", { query: { ...f } }),

  get: (id: number) => request<Company>(`/companies/${id}`).then(r => r.data),

  create: (body: CreateCompanyBody) =>
    request<Company>("/companies", { method: "POST", body }).then(r => r.data),

  update: (id: number, body: CreateCompanyBody) =>
    request<Company>(`/companies/${id}`, { method: "PATCH", body }).then(r => r.data),

  like: (id: number) => request<void>(`/companies/${id}/like`, { method: "POST" }),
  unlike: (id: number) => request<void>(`/companies/${id}/like`, { method: "DELETE" }),
};

// --------------------------------------------------------------------------
// events
// --------------------------------------------------------------------------

export interface EventFilter {
  status?: string;
  created_by?: number;
  location_id?: number;
  upcoming?: boolean;
  q?: string;
  page?: number;
  limit?: number;
}

export interface CreateEventBody {
  title: string;
  description: string;
  location_id?: number | null;
  start_time: string;
  end_time?: string | null;
}

export const events = {
  list: (f: EventFilter = {}) => request<Event[]>("/events", { query: { ...f } }),

  get: (id: number) => request<Event>(`/events/${id}`).then(r => r.data),

  create: (body: CreateEventBody) =>
    request<Event>("/events", { method: "POST", body }).then(r => r.data),

  update: (id: number, body: CreateEventBody) =>
    request<Event>(`/events/${id}`, { method: "PATCH", body }).then(r => r.data),

  cancel: (id: number) => request<void>(`/events/${id}`, { method: "DELETE" }),

  rsvp: (id: number, status: EventRSVPStatus) =>
    request<void>(`/events/${id}/rsvp`, { method: "POST", body: { status } }),

  participants: (id: number) =>
    request<EventParticipant[]>(`/events/${id}/participants`).then(r => r.data ?? []),
};

// --------------------------------------------------------------------------
// inquiries
// --------------------------------------------------------------------------

export const inquiries = {
  list: (page = 1, limit = 20) =>
    request<Inquiry[]>("/inquiries", { query: { page, limit } }),

  create: (body: {
    // Optional: the backend resolves the recipient from the entity (post
    // creator / company owner), so anonymous authors stay hidden.
    receiver_id?: number;
    entity_type: "post" | "company";
    entity_id: number;
    message?: string;
  }) => request<Inquiry>("/inquiries", { method: "POST", body }).then(r => r.data),

  get: (id: number) => request<Inquiry>(`/inquiries/${id}`).then(r => r.data),

  listMessages: (id: number) =>
    request<InquiryMessage[]>(`/inquiries/${id}/messages`).then(r => r.data ?? []),

  sendMessage: (id: number, message: string) =>
    request<InquiryMessage>(`/inquiries/${id}/messages`, {
      method: "POST",
      body: { message },
    }).then(r => r.data),

  markRead: (id: number) => request<void>(`/inquiries/${id}/read`, { method: "POST" }),
};

// --------------------------------------------------------------------------
// locations
// --------------------------------------------------------------------------

export const locations = {
  get: (id: number) => request<Location>(`/locations/${id}`).then(r => r.data),

  create: (body: {
    address_line1: string;
    address_line2?: string | null;
    city?: string | null;
    state_province?: string | null;
    district?: string | null;
    postal_code?: string | null;
    country_code: string;
    latitude?: number | null;
    longitude?: number | null;
  }) => request<Location>("/locations", { method: "POST", body }).then(r => r.data),
};

// --------------------------------------------------------------------------
// search
// --------------------------------------------------------------------------

export interface SearchResult {
  users?: User[];
  posts?: Post[];
  companies?: Company[];
  events?: Event[];
}

export const search = {
  query: (q: string, type?: "users" | "posts" | "companies" | "events", limit = 10) =>
    request<SearchResult>("/search", { query: { q, type, limit } }).then(r => r.data),
};

// --------------------------------------------------------------------------
// helpers: company type id <-> label mapping
// --------------------------------------------------------------------------

// The backend stores integer IDs for company_type (seed values from init.sql).
// The frontend carries the label; this map lets us round-trip between the two.
export const COMPANY_TYPE_IDS: Record<CompanyType, number> = {
  "Grocery and Convenience Store": 10,
  "Information Technology": 20,
  "Food and Restaurant": 30,
  "Personal Services": 40,
  Business: 50,
  Engineering: 60,
  Education: 70,
  "Finance and Insurance": 80,
  "Health Services": 90,
  "Hotels and Motels": 100,
  "Motor Vehicle": 110,
  "Agriculture and Wildlife": 120,
  Entertainment: 130,
  "Real Estate and Housing": 140,
  "Safety, Security and Legal": 150,
  Transportation: 160,
  Other: 170,
};

export type { ApiResult };
